import { useQuery } from "@tanstack/solid-query";
import { createContext, createMemo, createSignal, onMount } from "solid-js";
import { getUserStore } from "~/solidJs/entities/HeaderMain/models";
import { TeamMethods } from "~/solidJs/entities/Teams";
import { usePromoCodeFeature } from "~/solidJs/features/PromoCodeFeature/PromoCodeFeature";
import {
	getIsUserOnLegacyPlan,
	getUserSubscriptionLength,
	isUserFromRussia
} from "~/solidJs/shared";
import { TeamApi } from "~/solidJs/shared/api/TeamApi";
import { useTypedSearchParams, validateEnumValue } from "~/solidJs/shared/helpers/urlHelpres";
import { PricingPageParams } from "~/solidJs/shared/models/pageParams";
import type { PricingPageState } from "./model";

export type PricingPageContextType = ReturnType<typeof usePricingStore>;

export const PricingPageContext = createContext<PricingPageContextType>();

export const usePricingStore = () => {
	const [getParams, setParams] = useTypedSearchParams<PricingPageParams.Params>();
	const usePromocode = usePromoCodeFeature({});

	// Team info for determining active plan
	const teamInfo = useQuery(() => TeamApi.getTeamInfoOptions());

	// Legacy plans state
	const [getShowLegacyPlans, setShowLegacyPlans] = createSignal(false);

	// Getters
	const getSubscriptionType = () => {
		return validateEnumValue(
			getParams.type,
			PricingPageParams.SubscriptionType,
			PricingPageParams.SubscriptionType.Personal
		);
	};

	const getSubscriptionLength = () => {
		let defaultLength = PricingPageParams.SubscriptionLength.Year;
		const userLength = getUserSubscriptionLength(getUserStore());
		if (userLength === "1mo") {
			defaultLength = PricingPageParams.SubscriptionLength.Month;
		}
		return validateEnumValue(
			getParams.length,
			PricingPageParams.SubscriptionLength,
			defaultLength
		);
	};

	const getIsLengthYear = () => {
		return getSubscriptionLength() === PricingPageParams.SubscriptionLength.Year;
	};

	const getPaymentCurrency = () => {
		return validateEnumValue(
			getParams.currency,
			PricingPageParams.PaymentCurrency,
			isUserFromRussia(getUserStore()) || getUserStore()?.subscription === "yookassa"
				? PricingPageParams.PaymentCurrency.RUB
				: PricingPageParams.PaymentCurrency.USD
		);
	};

	const getPromo = () => {
		return getParams.promo || "";
	};

	const getIsRuCards = () => getPaymentCurrency() === PricingPageParams.PaymentCurrency.RUB;

	const getSotre = () => getParams;

	// Legacy plans getters
	const getIsUserOnLegacy = () => getIsUserOnLegacyPlan(getUserStore());

	const getShouldShowLegacyHeader = () => getIsUserOnLegacy();

	const getShouldShowLegacyPlans = () => getShowLegacyPlans();

	// Setters
	const setSubscriptionType = (type: PricingPageParams.SubscriptionType) => {
		setParams({ type });
	};

	const setSubscriptionLength = (length: PricingPageParams.SubscriptionLength) => {
		setParams({ length });
	};

	const setPaymentCurrency = (currency: PricingPageParams.PaymentCurrency) => {
		setParams({ currency });
	};

	const setPromo = (promo: string) => {
		setParams({ promo });
		usePromocode.applyPromocodeHandler(promo);
	};

	// Legacy plans setters
	const toggleLegacyPlans = () => {
		setShowLegacyPlans(!getShowLegacyPlans());
	};

	// Computed values
	const getState = (): PricingPageState => ({
		subscriptionType: getSubscriptionType(),
		subscriptionLength: getSubscriptionLength(),
		paymentCurrency: getPaymentCurrency(),
		promo: getPromo()
	});

	const isEnterprise = () =>
		getSubscriptionType() === PricingPageParams.SubscriptionType.Enterprise;

	/**
	 * Определяет активный план пользователя с учетом членства в команде
	 * Если пользователь член команды с активной подпиской, возвращает план команды
	 * Иначе возвращает личный план пользователя
	 */
	const getUserActivePlan = createMemo(() => {
		const user = getUserStore();
		const team = teamInfo.data;

		// Если нет пользователя, возвращаем null
		if (!user) return null;

		// Проверяем, является ли пользователь владельцем команды
		const isOwner = TeamMethods.isUserTeamOwner(team || null);

		// Проверяем, является ли пользователь членом команды
		const isMember = team?.members?.some((m) => m.user_id === user.id) ?? false;

		// Пользователь в команде если он владелец или член
		const isInTeam = isOwner || isMember;

		// Проверяем статус подписки команды
		const isTeamSubscriptionActive =
			team?.team_subscription_status === "active" ||
			team?.team_subscription_status === "cancelled" ||
			team?.team_subscription_status === "due";

		// Если пользователь в команде и подписка активна, используем тариф команды
		if (
			isInTeam &&
			isTeamSubscriptionActive &&
			team?.company_status === "active" &&
			team?.tariff_name
		) {
			// Нормализуем название тарифа к нижнему регистру для совместимости
			return team.tariff_name.toLowerCase();
		}

		// Иначе используем личный план пользователя
		return user.group;
	});

	// Initialize promo code on mount
	onMount(() => {
		const promo = getPromo();
		if (!promo) return;
		usePromocode.applyPromocodeHandler(promo);
	});

	return {
		// Getters
		getSubscriptionType,
		getSubscriptionLength,
		getPaymentCurrency,
		getPromo,
		getIsRuCards,
		getSotre,
		getState,
		isEnterprise,
		getIsLengthYear,
		getUserActivePlan,

		// Legacy plans getters
		getIsUserOnLegacy,
		getShouldShowLegacyHeader,
		getShouldShowLegacyPlans,

		// Setters
		setSubscriptionType,
		setSubscriptionLength,
		setPaymentCurrency,
		setPromo,

		// Legacy plans setters
		toggleLegacyPlans,

		// External hooks
		usePromocode,

		// Team info query
		teamInfo
	};
};
